import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { Config } from '../classes/config.class';
import { CoreConfig } from '../utils/config';
import { ApiService } from './api.service';
import { WebSocketService } from './web-socket.service';

const BASE_URL = 'lanes-config';

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  public config: Config;
  private _backupConfig: Config;

  private _configError: boolean;

  private _currentConfig: Subject<any> = new Subject<any>();

  public get currentConfig$(): Observable<any> {
    return this._currentConfig.asObservable();
  }

  /**
   * Is loading
   */
  public loading$: Observable<boolean>;

  /**
   * Constructor
   *
   * @param apiService ApiService
   */
  constructor(
    @Inject(CoreConfig.API_BASE_URL) public baseUrl: string,
    public httpClient: HttpClient,
    public wsService: WebSocketService,
    private apiService: ApiService
  ) {
    this.loading$ = this.apiService.loading$;
    this._configError = false;

    this.getConfig().subscribe(
      (config) => {
        if (config == null) {
          this._configError = true;
          return;
        }
        if (typeof config === 'string') {
          try {
            config = JSON.parse(config);
          } catch (err) {
            this._configError = true;
          }
        }
        if (config != null) {
          this.config = config;
          this._configError = false;
        }
      },
      () => {
        this._configError = true;
      }
    );
  }

  saveData(data: any) {
    if (data == null) {
      this._configError = true;
      return;
    }
    if (typeof data === 'string') {
      data = JSON.parse(data);
    }
    if (data != null) {
      this._configError = false;
      // @ts-ignore
      this.config = data;
      this.setCurrentConfig(data, false);
    }
  }

  getConfig(): Observable<any> {
    return this.apiService.get(`${BASE_URL}`).pipe(map((res) => res));
  }

  getBackupList(): Observable<string[]> {
    return this.apiService.get(`${BASE_URL}/list`).pipe(map((res) => res));
  }

  saveConfig(config: any): Observable<any> {
    return this.apiService.put(`${BASE_URL}`, config).pipe(map((res) => res));
  }

  applyConfiguration(): Observable<any> {
    this._backupConfig = this._backupConfig === null ? this.config : this._backupConfig;
    return this.saveConfig(this._backupConfig);
  }

  saveConfigWithBackup(config: any, backupFilename: string): Observable<any> {
    return this.apiService.put(`${BASE_URL}/save/${backupFilename}`, config).pipe(map((res) => res));
  }

  loadConfigBackup(backupFilename: string): Observable<any> {
    return this.apiService.post(`${BASE_URL}/load/${backupFilename}`).pipe(map((res) => res));
  }

  deleteConfigBackups(backupFiles: string[]): Observable<any> {
    return this.apiService.post(`${BASE_URL}/remove`, backupFiles).pipe(map((res) => res));
  }

  uploadConfiguration(file: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.httpClient.put(`${this.baseUrl}${BASE_URL}/upload`, formData).pipe(map((res) => res));
  }

  setCurrentConfig(config: any, fromBackup: boolean) {
    if (fromBackup) this._backupConfig = config;
    this._currentConfig.next(config);
  }

  // ===========================================
  //                  GETTERS
  // ===========================================

  getFullConfig() {
    return this.config;
  }

  getConfigError(): boolean {
    return this._configError;
  }

  getLanes() {
    return this.config.lanes;
  }

  getLane(laneId: number) {
    let searchedLane = null;
    for (const lan of this.config.lanes) {
      if (Number(lan.id) === Number(laneId)) {
        searchedLane = lan;
      }
    }
    return searchedLane;
  }

  getOCRServer(ocrServerId: number) {
    let seachedOCRServer = null;
    for (const server of this.config.ocr_servers) {
      if (Number(server.id) === Number(ocrServerId)) {
        seachedOCRServer = server;
      }
      return seachedOCRServer;
    }
  }

  getOCRServerIP(ocrServerId: number) {
    return this.getOCRServer(ocrServerId).IP;
  }

  getComServerIP() {
    return this.config.com_server.IP;
  }
  getAuthServerIP() {
    return this.config.auth_server.IP;
  }
  getServerIP() {
    return this.config.server.IP;
  }

  getOCRServerEventsDir(ocrServerId: number) {
    return this.getOCRServer(ocrServerId).eventsDir;
  }

  getOCRServerCameraURL(ocrServerId: number, id: number) {
    let url = '';
    this.getOCRServer(ocrServerId).cameras.forEach((camera) => {
      if (camera.id === id) {
        url = camera.url;
      }
    });

    return url;
  }

  // Se usa en el kiosko para mostrar si el carril es de entrada o de salida
  getLaneDirection(laneId: number): string {
    return this.config.lanes[laneId].general.direction;
  }

  // Se usa en el kiosko para mostrar si el carril está en modo automático, manual o bidireccional (kiosko samca)
  getLaneMode(laneId: number) {
    // TODO: Acabar funcion en base a parámetros que ha creado Miguel para ver si el carril está activado o no,
    // o si está en modo bidireccional o no
    return this.config.lanes[laneId].general;
  }

  setLane(laneId, configData) {
    this.apiService
      .patch(`${BASE_URL}/${laneId}`, { configData })
      .pipe(map((res) => res))
      .subscribe((data) => {
        this.saveData(data);
      });
  }
}
